import React, { createContext, useContext, useState, useEffect } from 'react';
import * as coupleService from '../services/coupleService';
import { useAuth } from './AuthContext';
import {
  canDeleteCouple,
  canEditCouple,
  isAdminRole,
  isCoupleAvailable
} from '../utils/couple/permissions';
import {
  formatCoupleAge as formatCoupleAgeValue,
  getActivePartner,
  getActivePartnerCount,
  getCoupleAge as getCoupleAgeValue
} from '../utils/couple/profile';

const CoupleContext = createContext();

export const useCouple = () => {
  const context = useContext(CoupleContext);
  if (!context) {
    throw new Error('useCouple deve ser usado dentro de um CoupleProvider');
  }
  return context;
};

export const CoupleProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [couple, setCouple] = useState(null);
  const [coupleLoading, setCoupleLoading] = useState(false);
  const [coupleError, setCoupleError] = useState(null);
  const [hasCouple, setHasCouple] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [coupleStats, setCoupleStats] = useState(null);

  // Carregar dados do casal quando usuário autentica
  useEffect(() => {
    if (isAuthenticated && user) {
      loadCoupleData();
    } else {
      clearCoupleData();
    }
  }, [isAuthenticated, user]);

  const loadCoupleData = async () => {
    if (coupleLoading) return;
    
    setCoupleLoading(true);
    setCoupleError(null);

    try {
      const response = await coupleService.getMyCouple();
      
      if (response.hasCouple) {
        setCouple(response.couple);
        setHasCouple(true);
        setUserRole(response.couple.userRole);
        
        // Carregar estatísticas se casal existe
        await loadCoupleStats();
      } else {
        setCouple(null);
        setHasCouple(false);
        setUserRole(null);
        setCoupleStats(null);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do casal:', error);
      setCoupleError(error.response?.data?.error || 'Erro ao carregar dados do casal');
      setCouple(null);
      setHasCouple(false);
      setUserRole(null);
    } finally {
      setCoupleLoading(false);
    }
  };

  const loadCoupleStats = async () => {
    try {
      const statsResponse = await coupleService.getCoupleStats();
      setCoupleStats(statsResponse.stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas do casal:', error);
      // Não é crítico, não vamos mostrar erro para o usuário
    }
  };

  const clearCoupleData = () => {
    setCouple(null);
    setHasCouple(false);
    setUserRole(null);
    setCoupleStats(null);
    setCoupleError(null);
  };

  const createCouple = async (coupleData) => {
    setCoupleLoading(true);
    setCoupleError(null);

    try {
      const response = await coupleService.createCouple(coupleData);
      setCouple(response.couple);
      setHasCouple(true);
      setUserRole('admin');
      
      // Recarregar estatísticas
      await loadCoupleStats();
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao criar casal';
      setCoupleError(errorMessage);
      throw error;
    } finally {
      setCoupleLoading(false);
    }
  };

  const invitePartner = async (email, message = '') => {
    setCoupleError(null);

    try {
      const response = await coupleService.invitePartner(email, message);
      // Recarregar dados do casal para atualizar convites pendentes
      await loadCoupleData();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao enviar convite';
      setCoupleError(errorMessage);
      throw error;
    }
  };

  const acceptInvitation = async (token, nickname = '') => {
    setCoupleLoading(true);
    setCoupleError(null);

    try {
      const response = await coupleService.acceptInvitation(token, nickname);
      
      // Atualizar estado local
      setCouple(response);
      setHasCouple(true);
      setUserRole('partner2');
      
      // Carregar estatísticas
      await loadCoupleStats();
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao aceitar convite';
      setCoupleError(errorMessage);
      throw error;
    } finally {
      setCoupleLoading(false);
    }
  };

  const updateCoupleSettings = async (settings) => {
    setCoupleError(null);

    try {
      const response = await coupleService.updateCoupleSettings(settings);
      setCouple(response.couple);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar configurações';
      setCoupleError(errorMessage);
      throw error;
    }
  };

  const leaveCouple = async () => {
    setCoupleLoading(true);
    setCoupleError(null);

    try {
      const response = await coupleService.leaveCouple();
      clearCoupleData();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao sair do casal';
      setCoupleError(errorMessage);
      throw error;
    } finally {
      setCoupleLoading(false);
    }
  };

  const refreshCoupleData = async () => {
    await loadCoupleData();
  };

  const refreshCoupleStats = async () => {
    await loadCoupleStats();
  };

  // Funções utilitárias
  const isPartner = () => isCoupleAvailable(hasCouple, couple);
  
  const isAdmin = () => isAdminRole(userRole);
  
  const canEdit = () => canEditCouple(hasCouple, couple, userRole);
  
  const canDelete = () => canDeleteCouple(hasCouple, couple, userRole);

  const getPartner = () => getActivePartner(couple, hasCouple, user);

  const getPartnerCount = () => getActivePartnerCount(couple, hasCouple);

  const getCoupleAge = () => getCoupleAgeValue(couple, hasCouple);

  const formatCoupleAge = () => formatCoupleAgeValue(getCoupleAge());

  const value = {
    // Estado
    couple,
    coupleLoading,
    coupleError,
    hasCouple,
    userRole,
    coupleStats,

    // Ações
    createCouple,
    invitePartner,
    acceptInvitation,
    updateCoupleSettings,
    leaveCouple,
    refreshCoupleData,
    refreshCoupleStats,

    // Utilitários
    isPartner,
    isAdmin,
    canEdit,
    canDelete,
    getPartner,
    getPartnerCount,
    getCoupleAge,
    formatCoupleAge,

    // Estado de erro
    setCoupleError,
    clearError: () => setCoupleError(null)
  };

  return (
    <CoupleContext.Provider value={value}>
      {children}
    </CoupleContext.Provider>
  );
};

export default CoupleContext;